import dayjs from 'dayjs'
import { DeviceReportSearchLog, DeviceReportSearchLogRow, SearchLogResponse } from '../interfaces'
import { getDeviceActionToLabelMap } from '../components/reportTypeOptions'
import notify from '~/utils/notify'

// Função para validar parâmetros de busca
export const validateSearchParams = (startDate: string | null, finishDate: string | null, translate: (key: string) => string): boolean => {
  if (!startDate || !finishDate) {
    notify({
      type: 'error',
      message: translate('Start date and end date must be provided'),
    })
    return false
  }
  return true
}

// Função para buscar logs de relatório de dispositivo
export const fetchDeviceReportLogs = async (params: {
  readonly user_id?: number
  readonly start_date: string
  readonly finish_date: string
  readonly action?: string | null
  readonly origin?: 'device' | 'station' | null
  readonly device_id?: number | null
  readonly station_id?: number | null
  readonly page: number
  readonly limit: number
  readonly orders: readonly { readonly field: string; readonly sort: string }[]
}): Promise<SearchLogResponse> => {
  const { getDeviceActionsLogs } = await import('../services')
  return getDeviceActionsLogs(params)
}

// Função para mapear dados de log para linhas da tabela
export const mapSearchLogDataToRows = (
  data: readonly DeviceReportSearchLog[],
  page: number,
  pageSize: number,
  translate: (key: string) => string,
  getReportNameFromRoute: (route?: string) => string,
  extractDatesFromContent: (content: string) => { readonly start_date: string | null; readonly finish_date: string | null }
): readonly DeviceReportSearchLogRow[] => {
  // Filtrar logs com GET ou action 'view'
  const filteredData = data.filter((element) => {
    const method = (element.request_type ?? '').toString().toUpperCase()
    const action = (element.action ?? '').toString().toLowerCase()
    return method !== 'GET' && action !== 'view'
  })

  return filteredData.map((element: DeviceReportSearchLog, index: number) => {
    const generatedId = element.id ?? page * pageSize + index + 1

    const reportName = element.route
      ? getReportNameFromRoute(element.route)
      : translate('Unknown Report')

    const actionMap = getDeviceActionToLabelMap(translate)
    let actionLabel = element.action ? (actionMap[element.action as keyof typeof actionMap] ?? reportName) : reportName

    if (!element.action && (element.route ?? '').toString().includes('/device/') && actionLabel === element.route) {
      const method = (element.request_type ?? '').toString().toUpperCase()
      if (method === 'PUT') {
        actionLabel = actionMap['edit' as keyof typeof actionMap] ?? actionLabel
      } else if (method === 'POST') {
        actionLabel = actionMap['create' as keyof typeof actionMap] ?? actionLabel
      }
    }

    const {
      start_date: contentStartDate,
      finish_date: contentFinishDate,
    } = element.content
      ? extractDatesFromContent(element.content)
      : { start_date: null, finish_date: null }

    return {
      id: generatedId,
      ...element,
      'user:representation_name': element?.user_name || '',
      report_display: reportName,
      action_display: actionLabel,
      origin: element?.station
        ? `Estação - ${element?.station}`
        : element?.device
        ? `Dispositivo - ${element?.device}`
        : '',
      content_start_date: contentStartDate,
      content_finish_date: contentFinishDate,
      content_period:
        contentStartDate || contentFinishDate
          ? `${
              contentStartDate
                ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm')
                : ''
            }${contentStartDate && contentFinishDate ? ' - ' : ''}${
              contentFinishDate
                ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm')
                : ''
            }`
          : null,
    } as DeviceReportSearchLogRow
  })
}